/**
 * Per-context topbar toggle row. Mockup behaviour: only the active context's
 * pill set is shown. Each pill is wired to a store slice so it has real
 * map effect (mockup's `tgl-drones / tgl-oi / tgl-surv / tgl-infra /
 * tgl-rtm / tgl-inv` groups).
 */
import { useMqttState } from '@/features/data-stream/hooks/useMqttState';
import type { DsFilter } from '@/features/data-stream/types';
import { useInvStore } from '@/features/inv/InvStore';
import { canViewContext } from '@/lib/auth/access';
import { config } from '@/lib/config';
import { SERVICE_LABEL, orderServices, serviceLabel } from '@/lib/mqtt/schemas/surveillance';
import { RTM_STATUSES } from '@/lib/oi/rtmStatus';
import { useCurrentRole } from '@/lib/store/auth';
import { useDataStreamStore } from '@/lib/store/dataStream';
import { useInfraStore } from '@/lib/store/infra';
import { type OiStatusFilter, useOiStore } from '@/lib/store/oi';
import { type RtmFilter, useRtmStore } from '@/lib/store/rtm';
import { useSurvStore } from '@/lib/store/surv';
import { useUiStore } from '@/lib/store/ui';
import { cn } from '@/lib/utils';
import { useShallow } from 'zustand/shallow';
import { DRONE_TOGGLES } from './droneToggles';

/** Broker host shown in the Data Stream connection chip (e.g.
 *  `emqx-ws.airmarket.stage.flysafe.live`), derived from the configured URL. */
const BROKER_HOST = (() => {
  try {
    return config.mqtt.url ? new URL(config.mqtt.url).hostname : '';
  } catch {
    return '';
  }
})();

// One chip per rtm_state status (ENG-213).
const OI_FILTERS: readonly OiStatusFilter[] = RTM_STATUSES;
const RTM_FILTERS: { k: RtmFilter; label: string }[] = [
  { k: 'healthy', label: 'Healthy' },
  { k: 'degraded', label: 'Degraded' },
  { k: 'offline', label: 'Offline' },
];

export function ContextToggles() {
  const ctx = useUiStore((s) => s.activeContext);
  const role = useCurrentRole();
  // ENG-280: a locked context shows the login gate, so its filter pills would be
  // dead controls over a blank page. Same rule BodyGrid uses to gate the body.
  if (!canViewContext(ctx, role)) return null;
  if (ctx === 'drones') return <DroneToggles />;
  if (ctx === 'oi') return <OIToggles />;
  if (ctx === 'surv') return <SurvToggles />;
  if (ctx === 'infra') return <InfraToggles />;
  if (ctx === 'rtm') return <RtmToggles />;
  if (ctx === 'inv') return <InvToggles />;
  if (ctx === 'ds') return <DsToggles />;
  // Apps context has no topbar pills (matches mockup — full-area overlay only).
  return null;
}

/* ─────────────────── DRONES ─────────────────── */
function DroneToggles() {
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {DRONE_TOGGLES.map((t, i) => (
        <DroneTogglePill
          key={t.key}
          k={t.key}
          label={t.label}
          dividerBefore={i > 0 && (t as { divider?: string }).divider === 'before'}
        />
      ))}
    </div>
  );
}
function DroneTogglePill({
  k,
  label,
  dividerBefore,
}: { k: (typeof DRONE_TOGGLES)[number]['key']; label: string; dividerBefore?: boolean }) {
  const on = useUiStore((s) => s[k]);
  const toggle = useUiStore((s) => s.toggle);
  return (
    <>
      {dividerBefore && <Divider />}
      <Pill on={on} onClick={() => toggle(k)} label={label} />
    </>
  );
}

/* ─────────────────── OI ─────────────────── */
function OIToggles() {
  const showDrone = useUiStore((s) => s.showOIDrone);
  const showPred = useUiStore((s) => s.showOIDronePred);
  const showGeofences = useUiStore((s) => s.showGeofences);
  const toggle = useUiStore((s) => s.toggle);
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <Pill on={showDrone} onClick={() => toggle('showOIDrone')} label="Drone" />
      <Pill on={showPred} onClick={() => toggle('showOIDronePred')} label="Drone Projection" />
      <Divider />
      {OI_FILTERS.map((f) => (
        <OIFilterPill key={f} f={f} />
      ))}
      <Divider />
      <Pill on={showGeofences} onClick={() => toggle('showGeofences')} label="Geofences" />
    </div>
  );
}
function OIFilterPill({ f }: { f: OiStatusFilter }) {
  const on = useOiStore((s) => s.filters[f]);
  const toggleFilter = useOiStore((s) => s.toggleFilter);
  return <Pill on={on} onClick={() => toggleFilter(f)} label={f} />;
}

/* ─────────────────── SURV ─────────────────── */
/**
 * Single SERVICE row (mockup `#tgl-surv`). The old VIEW row is folded in:
 * "All Services" is the combined overview that "Fused View" used to give, and
 * picking a service opens its detail — what Sensor Diagnostic + a service pill
 * used to take two clicks to reach.
 *
 * Radio behaviour, per the mockup's `.tgl-radio`: clicking the selected pill
 * keeps it selected. "All Services" is the way back out.
 */
function SurvToggles() {
  const setMode = useSurvStore((s) => s.setMode);
  const selectedNet = useSurvStore((s) => s.selectedNetwork);
  const setNetwork = useSurvStore((s) => s.setNetwork);
  const fusedRunning = useSurvStore((s) => s.fusedService != null);
  // The buttons follow the services the broker reports — curated ones first, in
  // their fixed order, then anything newly discovered A→Z.
  const serviceIds = useSurvStore(useShallow((s) => orderServices(Object.keys(s.byId))));

  const pickAll = () => {
    setMode('fused');
    setNetwork(null);
  };
  // setMode('diagnostic') defaults the network to live_ads; setNetwork right
  // after pins the one actually clicked.
  const pickService = (id: string) => {
    setMode('diagnostic');
    setNetwork(id);
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <Label>SERVICE</Label>
      <Pill on={selectedNet === null} onClick={pickAll} label="All Services" />
      <Divider />
      {serviceIds.map((n) => (
        <Pill
          key={n}
          on={selectedNet === n}
          onClick={() => pickService(n)}
          label={serviceLabel(n)}
        />
      ))}
      {/* Live_Fused is not a broker emitter — it exists only while the fusion
          is running, so its chip comes and goes with it. */}
      {fusedRunning && (
        <>
          <Divider />
          <Pill
            on={selectedNet === 'live_fused'}
            onClick={() => pickService('live_fused')}
            label={SERVICE_LABEL.live_fused}
          />
        </>
      )}
    </div>
  );
}

/* ─────────────────── INFRA ─────────────────── */
function InfraToggles() {
  const filterStatus = useInfraStore((s) => s.filterStatus);
  const filterType = useInfraStore((s) => s.filterType);
  const toggleStatus = useInfraStore((s) => s.toggleStatus);
  const toggleType = useInfraStore((s) => s.toggleType);
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <Label>STATUS</Label>
      <Pill
        on={filterStatus.active}
        onClick={() => toggleStatus('active')}
        label="Online"
        dotColor="#22c55e"
      />
      <Pill
        on={filterStatus.degraded}
        onClick={() => toggleStatus('degraded')}
        label="Degraded"
        dotColor="#f59e0b"
      />
      <Pill
        on={filterStatus.inactive}
        onClick={() => toggleStatus('inactive')}
        label="Offline"
        dotColor="#ef4444"
      />
      <Divider />
      <Label>TYPE</Label>
      <Pill on={filterType.F} onClick={() => toggleType('F')} label="Fixed" />
      <Pill on={filterType.M} onClick={() => toggleType('M')} label="Mobile" />
    </div>
  );
}

/* ─────────────────── RTM ─────────────────── */
function RtmToggles() {
  const filters = useRtmStore((s) => s.filters);
  const toggleFilter = useRtmStore((s) => s.toggleFilter);
  const byName = useRtmStore((s) => s.byName);
  const counts = {
    healthy: Object.values(byName).filter((p) => p.pod_status === 'running').length,
    degraded: Object.values(byName).filter((p) => p.pod_status === 'degraded').length,
    offline: Object.values(byName).filter((p) => p.pod_status === 'down').length,
  };
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <Label>SHOW</Label>
      {RTM_FILTERS.map(({ k, label }) => (
        <Pill
          key={k}
          on={filters[k]}
          onClick={() => toggleFilter(k)}
          label={`${label} ${counts[k]}`}
        />
      ))}
    </div>
  );
}

/* ─────────────────── INV ─────────────────── */
function InvToggles() {
  const view = useInvStore((s) => s.view);
  const setView = useInvStore((s) => s.setView);
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <Label>VIEW</Label>
      <Pill on={view === 'users'} onClick={() => setView('users')} label="Users" />
      <Pill on={view === 'drones'} onClick={() => setView('drones')} label="Drones" />
    </div>
  );
}

/* ─────────────────── DATA STREAM ─────────────────── */
const DS_FILTERS: { k: DsFilter; label: string }[] = [
  { k: 'all', label: 'All' },
  { k: 'aircraft', label: 'Aircraft' },
  { k: 'surveillance', label: 'Surveillance' },
  { k: 'traffic', label: 'Traffic' },
  { k: 'events', label: 'Events' },
  { k: 'alerts', label: 'Alerts' },
  { k: 'oi', label: 'OI' },
];

function DsToggles() {
  const filter = useDataStreamStore((s) => s.filter);
  const setFilter = useDataStreamStore((s) => s.setFilter);
  const paused = useDataStreamStore((s) => s.paused);
  const togglePause = useDataStreamStore((s) => s.togglePause);
  const clear = useDataStreamStore((s) => s.clear);
  // "Subscribe all (#)" — added feature, not in the mockup. Commented out for
  // now (re-enable the selectors + the Pill below to restore). The store still
  // supports it (wildcardOn / setWildcard) so nothing else needs changing.
  // const wildcardOn = useDataStreamStore((s) => s.wildcardOn);
  // const setWildcard = useDataStreamStore((s) => s.setWildcard);
  const state = useMqttState();

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {DS_FILTERS.map((f) => (
        <Pill key={f.k} on={filter === f.k} onClick={() => setFilter(f.k)} label={f.label} />
      ))}
      <Divider />
      <Pill on={paused} onClick={togglePause} label={paused ? 'Paused' : 'Pause'} />
      <Pill on={false} onClick={clear} label="Clear" />
      {/* <Pill on={wildcardOn} onClick={() => setWildcard(!wildcardOn)} label="Subscribe all (#)" /> */}
      <Divider />
      <span
        className={cn(
          'flex items-center gap-1.5 rounded border px-2.5 py-[2px] font-mono text-[0.731em] whitespace-nowrap',
          state === 'connected'
            ? 'border-(--color-green) bg-(--color-green-bg) text-(--color-green)'
            : 'border-(--color-border) text-(--color-text3)',
        )}
      >
        <span
          className={cn(
            'h-[5px] w-[5px] rounded-full',
            state === 'connected' ? 'bg-(--color-green)' : 'bg-(--color-text3)',
          )}
        />
        {BROKER_HOST ? `${BROKER_HOST} · ${state}` : state}
      </span>
    </div>
  );
}

/* ─────────────────── primitives ─────────────────── */
function Pill({
  on,
  onClick,
  label,
  dotColor,
}: { on: boolean; onClick: () => void; label: string; dotColor?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 h-[30px] px-[11px] rounded-[15px] border text-[0.846em] font-medium whitespace-nowrap transition-[background-color,color,border-color] duration-150 select-none',
        on
          ? 'bg-(--color-selected) border-(--color-selected) text-(--color-selected-fg)'
          : 'bg-(--color-bg) border-(--color-border) text-(--color-text2) hover:border-(--color-green)',
      )}
    >
      <span
        className="size-1.5 rounded-full"
        style={{ backgroundColor: dotColor ?? 'currentColor', opacity: dotColor ? 1 : 0.6 }}
      />
      {label}
    </button>
  );
}

function Divider() {
  return <span className="w-px h-[22px] bg-(--color-border) shrink-0 mx-1" />;
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[0.692em] font-bold tracking-wider text-(--color-text3) uppercase shrink-0">
      {children}
    </span>
  );
}
